/**
 * combat/pvp.ts — PvP Flagging, Death, and Respawn Systems
 *
 * Toggle PvP → become attackable by other flagged players.
 * Attack someone → become aggressor (name turns red, 10min cooldown).
 * Die → enter ghost mode → walk to respawn point → resurrect.
 * Graveyard experience: ethereal, desaturated, can see but not interact.
 */

import type { GameState, PvPState, EntityDisposition } from "../engine/state.js";
import { addToast } from "../render/toasts.js";
import { getCreatureHp, getCreatureCombatStats } from "./system.js";

// ─── Constants ───────────────────────────────────────────────────────────────

const PVP_TOGGLE_COOLDOWN  = 600;    // 10 minutes in seconds
const AGGRESSOR_DURATION   = 600;    // 10 minutes in seconds
const RESPAWN_TIMER_BASE   = 10;     // base 10 second respawn timer
const RESPAWN_TIMER_STACK  = 5;      // +5s per death this session (diminishing)
const RESPAWN_TIMER_MAX    = 30;     // cap at 30 seconds
const GHOST_SPEED_MULT     = 1.3;    // ghosts move 30% faster
const GHOST_ALPHA          = 0.35;   // ghost translucency
const XP_LOSS_PERCENT      = 0.05;   // 5% of XP to next level lost on PvE death
const XP_LOSS_PVP_PERCENT  = 0.02;   // 2% on PvP death (less harsh)
const DURABILITY_LOSS       = 0;     // reserved for future gear durability

// Suppress unused-variable lint for reserved constant
void DURABILITY_LOSS;

// ─── PvP Toggle ──────────────────────────────────────────────────────────────

/** Toggle PvP flag on/off. Returns a status message for toast display. */
export function togglePvP(state: GameState): string {
  const pvp = state.player.pvp;
  const now = performance.now() / 1000;

  if (pvp.flagged) {
    // Trying to unflag
    if (now < pvp.cooldownUntil) {
      const remaining = Math.ceil(pvp.cooldownUntil - now);
      const mins = Math.floor(remaining / 60);
      const secs = remaining % 60;
      return `PvP cooldown: ${mins}:${String(secs).padStart(2, "0")} remaining`;
    }
    // Can unflag
    pvp.flagged = false;
    pvp.aggressor = false;
    pvp.aggressorUntil = 0;
    return "PvP disabled";
  } else {
    // Flag on
    pvp.flagged = true;
    pvp.flaggedAt = now;
    pvp.cooldownUntil = now + PVP_TOGGLE_COOLDOWN;
    return "PvP enabled — you can be attacked by other flagged players";
  }
}

/** Mark the player as an aggressor (attacked first). */
export function markAggressor(state: GameState): void {
  const pvp = state.player.pvp;
  const now = performance.now() / 1000;
  pvp.aggressor = true;
  pvp.aggressorUntil = now + AGGRESSOR_DURATION;
  pvp.cooldownUntil = Math.max(pvp.cooldownUntil, now + PVP_TOGGLE_COOLDOWN);
  pvp.lastPvpCombatAt = now;
}

/** Check if player can attack another entity based on PvP rules. */
export function canAttackPlayer(attacker: PvPState, targetPvpFlagged: boolean): boolean {
  // Both must be PvP flagged
  return attacker.flagged && targetPvpFlagged;
}

/** Get the disposition color category for another player. */
export function getPlayerDisposition(
  myPvp: PvPState,
  targetPvpFlagged: boolean,
  targetPvpAggressor: boolean,
): EntityDisposition {
  if (targetPvpAggressor) return "pvp_aggressor";  // red — they attacked someone
  if (targetPvpFlagged && myPvp.flagged) return "pvp_flagged";  // orange — mutual PvP
  if (targetPvpFlagged) return "attackable";  // yellow — they're flagged, you're not
  return "friendly";  // green — safe
}

/** Update PvP timers each frame. */
export function updatePvP(state: GameState, _dt: number): void {
  void _dt;
  const pvp = state.player.pvp;
  const now = performance.now() / 1000;

  // Expire aggressor status
  if (pvp.aggressor && now >= pvp.aggressorUntil) {
    pvp.aggressor = false;
    pvp.aggressorUntil = 0;
  }
}

// ─── Death System ────────────────────────────────────────────────────────────

/** Kill the player. Called when HP reaches 0. */
export function killPlayer(state: GameState, killedByPvP: boolean): void {
  const death = state.player.death;
  const now = performance.now() / 1000;

  death.isDead = true;
  death.diedAt = now;
  death.ghostMode = true;
  death.ghostAlpha = GHOST_ALPHA;
  death.deathCount++;

  // Calculate respawn timer (stacking penalty, capped)
  const stackPenalty = Math.min(death.deathCount - 1, 4) * RESPAWN_TIMER_STACK;
  death.respawnTimer = Math.min(RESPAWN_TIMER_BASE + stackPenalty, RESPAWN_TIMER_MAX);
  death.canRespawn = false;

  // Apply death penalty
  const combat = state.player.combat;
  if (killedByPvP) {
    // PvP death: lighter XP penalty
    death.deathPenalty = "xp";
    const xpLoss = Math.floor(combat.xpToNext * XP_LOSS_PVP_PERCENT);
    death.xpLost = xpLoss;
    combat.xp = Math.max(0, combat.xp - xpLoss);
    state.player.pvp.deaths++;
  } else {
    // PvE death: standard XP penalty
    death.deathPenalty = "xp";
    const xpLoss = Math.floor(combat.xpToNext * XP_LOSS_PERCENT);
    death.xpLost = xpLoss;
    combat.xp = Math.max(0, combat.xp - xpLoss);
  }

  // Set respawn location (current zone's safe point)
  death.respawnLocation = {
    tileX: 3,
    tileY: 3,
    zoneId: state.world.currentZoneId,
  };

  // End any active combat
  state.player.combatState.active = false;
  state.player.combatState.targetId = null;
  state.player.combatState.combo = 0;

  // Clear target
  state.player.target.id = null;
  state.player.target.type = null;
}

/** Update death/respawn state each frame. */
export function updateDeath(state: GameState, dt: number): void {
  const death = state.player.death;
  if (!death.isDead) return;

  // Count down respawn timer
  if (death.respawnTimer > 0) {
    death.respawnTimer = Math.max(0, death.respawnTimer - dt);
    if (death.respawnTimer <= 0) {
      death.canRespawn = true;
    }
  }

  // Ghost visual pulsing — gentle alpha oscillation
  if (death.ghostMode) {
    const now = performance.now() / 1000;
    const pulse = Math.sin((now - death.diedAt) * 2) * 0.05;
    death.ghostAlpha = GHOST_ALPHA + pulse;
  }
}

/** Respawn the player. Can only be called when canRespawn is true. */
export function respawnPlayer(state: GameState): boolean {
  const death = state.player.death;
  if (!death.isDead || !death.canRespawn) return false;

  // Restore HP to 50%
  const combat = state.player.combat;
  combat.hp = Math.ceil(combat.maxHp * 0.5);

  // Restore EP to 50%
  combat.ember.current = Math.ceil(combat.ember.max * 0.5);

  // Clear death state
  death.isDead = false;
  death.ghostMode = false;
  death.ghostAlpha = 1.0;
  death.canRespawn = false;
  death.respawnTimer = 0;

  // Move to respawn location
  if (death.respawnLocation) {
    state.player.position.tileX = death.respawnLocation.tileX;
    state.player.position.tileY = death.respawnLocation.tileY;
    // If different zone, the game loop will handle zone transition
  }

  state.player.animState = "idle";

  // Clear death recap
  state.player.deathRecap.visible = false;
  state.player.deathRecap.entries = [];

  return true;
}

/** Get the ghost movement speed multiplier. */
export function getGhostSpeedMult(state: GameState): number {
  return state.player.death.ghostMode ? GHOST_SPEED_MULT : 1.0;
}

// ─── Target Selection ────────────────────────────────────────────────────────

/** Select a creature as the current target. */
export function targetCreature(state: GameState, creatureId: string): void {
  const creature = state.creatures.find(c => c.id === creatureId);
  if (!creature) return;

  const target = state.player.target;
  target.id = creatureId;
  target.type = "creature";
  target.name = creature.name;
  target.level = 1;  // creatures derive difficulty from zone danger level, not individual level
  target.disposition = "hostile";
  target.selectedAt = performance.now() / 1000;

  // Calculate distance
  const dx = creature.position.tileX - state.player.position.tileX;
  const dy = creature.position.tileY - state.player.position.tileY;
  target.distance = Math.sqrt(dx * dx + dy * dy);
}

/** Select an NPC as the current target. */
export function targetNpc(state: GameState, npcId: string): void {
  const npc = state.npcs.find(n => n.id === npcId);
  if (!npc) return;

  const target = state.player.target;
  target.id = npcId;
  target.type = "npc";
  target.name = npc.name;
  target.level = 0;
  target.hp = 0;
  target.maxHp = 0;
  target.disposition = "friendly";
  target.selectedAt = performance.now() / 1000;

  const dx = npc.position.tileX - state.player.position.tileX;
  const dy = npc.position.tileY - state.player.position.tileY;
  target.distance = Math.sqrt(dx * dx + dy * dy);
}

/** Select another player as the current target. */
export function targetPlayer(state: GameState, playerId: string): void {
  const other = state.otherPlayers.find(p => p.id === playerId);
  if (!other) return;

  const target = state.player.target;
  target.id = playerId;
  target.type = "player";
  target.name = other.name;
  target.level = other.combat.level;
  target.hp = other.combat.hp;
  target.maxHp = other.combat.maxHp;
  target.disposition = getPlayerDisposition(
    state.player.pvp,
    other.pvpFlagged,
    other.pvpAggressor,
  );
  target.selectedAt = performance.now() / 1000;

  const dx = other.position.tileX - state.player.position.tileX;
  const dy = other.position.tileY - state.player.position.tileY;
  target.distance = Math.sqrt(dx * dx + dy * dy);
}

/** Clear the current target. */
export function clearTarget(state: GameState): void {
  const target = state.player.target;
  target.id = null;
  target.type = null;
  target.name = null;
  target.level = 0;
  target.hp = 0;
  target.maxHp = 0;
  target.disposition = "neutral";
  target.distance = 0;
}

/** Update target distance and HP each frame. */
export function updateTarget(state: GameState): void {
  const target = state.player.target;
  if (!target.id) return;

  if (target.type === "creature") {
    const creature = state.creatures.find(c => c.id === target.id);
    if (!creature) {
      clearTarget(state);
      return;
    }
    // Refresh combat HP from the external tracker
    const combatHp = getCreatureHp(creature.id);
    if (combatHp) {
      target.hp = combatHp.hp;
      target.maxHp = combatHp.maxHp;
    } else {
      // Not in combat — show full HP from stats
      const zoneId = state.world?.currentZoneId;
      let dangerLevel = 0;
      if (zoneId && state.world.zones) {
        const zone = state.world.zones.find(z => z.id === zoneId);
        if (zone) dangerLevel = zone.dangerLevel;
      }
      const stats = getCreatureCombatStats(creature, dangerLevel);
      target.hp = stats.maxHp;
      target.maxHp = stats.maxHp;
    }
    const dx = creature.position.tileX - state.player.position.tileX;
    const dy = creature.position.tileY - state.player.position.tileY;
    target.distance = Math.sqrt(dx * dx + dy * dy);
  } else if (target.type === "player") {
    const other = state.otherPlayers.find(p => p.id === target.id);
    if (!other) {
      clearTarget(state);
      return;
    }
    target.hp = other.combat.hp;
    target.maxHp = other.combat.maxHp;
    target.disposition = getPlayerDisposition(
      state.player.pvp,
      other.pvpFlagged,
      other.pvpAggressor,
    );
    const dx = other.position.tileX - state.player.position.tileX;
    const dy = other.position.tileY - state.player.position.tileY;
    target.distance = Math.sqrt(dx * dx + dy * dy);
  } else if (target.type === "npc") {
    const npc = state.npcs.find(n => n.id === target.id);
    if (!npc) {
      clearTarget(state);
      return;
    }
    const dx = npc.position.tileX - state.player.position.tileX;
    const dy = npc.position.tileY - state.player.position.tileY;
    target.distance = Math.sqrt(dx * dx + dy * dy);
  }
}

// ─── Duel System ─────────────────────────────────────────────────────────────

const DUEL_CHALLENGE_TIMEOUT = 30;     // seconds to accept/decline
const DUEL_COUNTDOWN         = 3;      // 3-2-1 before fight starts
const DUEL_MAX_DISTANCE      = 4;      // max tile distance to initiate

// Suppress unused-variable lint for reserved constant
void DUEL_CHALLENGE_TIMEOUT;

/** Send a duel challenge to another player. */
export function challengeToDuel(state: GameState, targetId: string): string {
  const other = state.otherPlayers.find(p => p.id === targetId);
  if (!other) return "Player not found";

  if (state.player.duel.active) return "Already in a duel";
  if (state.player.death.isDead) return "Cannot duel while dead";
  if (state.player.combatState.active) return "Cannot duel while in combat";

  const dx = other.position.tileX - state.player.position.tileX;
  const dy = other.position.tileY - state.player.position.tileY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist > DUEL_MAX_DISTANCE) return "Too far away to duel";

  // In a real multiplayer scenario, this would send a network message.
  // For now, simulate: set the other player's pending challenge.
  // The UI will show the challenge notification.
  return `Duel request sent to ${other.name}`;
}

/** Accept an incoming duel challenge. */
export function acceptDuel(state: GameState): string {
  const challenge = state.player.duel.pendingChallenge;
  if (!challenge) return "No pending duel challenge";

  const now = performance.now() / 1000;
  if (now > challenge.expiresAt) {
    state.player.duel.pendingChallenge = null;
    return "Duel challenge expired";
  }

  // Start countdown
  state.player.duel.active = true;
  state.player.duel.opponentId = challenge.challengerId;
  state.player.duel.opponentName = challenge.challengerName;
  state.player.duel.countdown = DUEL_COUNTDOWN;
  state.player.duel.countdownStart = now;
  state.player.duel.startedAt = 0;
  state.player.duel.winnerId = null;
  state.player.duel.pendingChallenge = null;

  return `Duel accepted! ${DUEL_COUNTDOWN}...`;
}

/** Decline an incoming duel challenge. */
export function declineDuel(state: GameState): void {
  state.player.duel.pendingChallenge = null;
}

/** Update duel state each frame. */
export function updateDuel(state: GameState, dt: number): void {
  const duel = state.player.duel;

  // Expire pending challenges
  if (duel.pendingChallenge) {
    const now = performance.now() / 1000;
    if (now > duel.pendingChallenge.expiresAt) {
      duel.pendingChallenge = null;
    }
  }

  if (!duel.active) return;

  // Countdown phase
  if (duel.countdown > 0) {
    duel.countdown = Math.max(0, duel.countdown - dt);
    if (duel.countdown <= 0) {
      // Fight begins
      duel.startedAt = performance.now() / 1000;
    }
    return;
  }

  // Active duel — check win condition (opponent reaches 1 HP)
  // In a real multiplayer game, damage is applied normally but clamped to 1 HP
  // For now, check if our HP hit 1 (we lost) or opponent disappeared
  if (state.player.combat.hp <= 1) {
    endDuel(state, duel.opponentId ?? "unknown");
  }
}

/** Clamp HP to 1 during a duel instead of killing. */
export function duelDamageClamp(state: GameState, damage: number): number {
  if (!state.player.duel.active || state.player.duel.countdown > 0) return damage;

  const hp = state.player.combat.hp;
  // If this damage would kill us, clamp to leave us at 1 HP
  if (hp - damage <= 0) {
    return hp - 1; // leave at 1 HP
  }
  return damage;
}

/** End a duel with a winner. */
export function endDuel(state: GameState, winnerId: string): void {
  const duel = state.player.duel;
  duel.winnerId = winnerId;

  // Ensure loser stays at 1 HP (not dead)
  if (state.player.combat.hp <= 1) {
    state.player.combat.hp = 1;
  }

  // End combat state
  state.player.combatState.active = false;
  state.player.combatState.targetId = null;

  // Clean up after 3 seconds (show result)
  setTimeout(() => {
    duel.active = false;
    duel.opponentId = null;
    duel.opponentName = null;
    duel.countdown = 0;
    duel.winnerId = null;
  }, 3000);
}

/** Forfeit (flee) a duel. Counts as a loss. */
export function forfeitDuel(state: GameState): void {
  if (!state.player.duel.active) return;
  endDuel(state, state.player.duel.opponentId ?? "unknown");
}

// ─── Context Menu ────────────────────────────────────────────────────────────

/** Open context menu for a creature. */
export function openCreatureContextMenu(state: GameState, creatureId: string, screenX: number, screenY: number): void {
  const creature = state.creatures.find(c => c.id === creatureId);
  if (!creature) return;

  const menu = state.contextMenu;
  menu.open = true;
  menu.x = screenX;
  menu.y = screenY;
  menu.targetId = creatureId;
  menu.targetType = "creature";
  menu.targetName = creature.name;
  menu.options = [
    { id: "attack", label: "Attack", icon: "\u2694", enabled: !state.player.death.isDead },
    { id: "target", label: "Target", icon: "\u25ce", enabled: true },
    { id: "set_focus", label: "Set Focus", icon: "\ud83d\udd2d", enabled: true },
    { id: "inspect", label: "Inspect", icon: "\ud83d\udd0d", enabled: true },
    { id: "mark_skull", label: "Mark: Skull", icon: "\ud83d\udc80", enabled: true },
    { id: "mark_cross", label: "Mark: Cross", icon: "\u2716", enabled: true },
    { id: "mark_star", label: "Mark: Star", icon: "\u2b50", enabled: true },
  ];
}

/** Open context menu for an NPC. */
export function openNpcContextMenu(state: GameState, npcId: string, screenX: number, screenY: number): void {
  const npc = state.npcs.find(n => n.id === npcId);
  if (!npc) return;

  const menu = state.contextMenu;
  menu.open = true;
  menu.x = screenX;
  menu.y = screenY;
  menu.targetId = npcId;
  menu.targetType = "npc";
  menu.targetName = npc.name;
  const canSpar = !state.player.death.isDead && !state.player.duel.active && !state.player.combatState.active;
  menu.options = [
    { id: "talk", label: "Talk", icon: "\ud83d\udcac", enabled: true },
    { id: "spar", label: "Spar", icon: "\u2694", enabled: canSpar },
    { id: "inspect", label: "Inspect", icon: "\ud83d\udd0d", enabled: true },
    { id: "target", label: "Target", icon: "\u25ce", enabled: true },
    { id: "set_focus", label: "Set Focus", icon: "\ud83d\udd2d", enabled: true },
  ];
}

/** Open context menu for another player. */
export function openPlayerContextMenu(state: GameState, playerId: string, screenX: number, screenY: number): void {
  const other = state.otherPlayers.find(p => p.id === playerId);
  if (!other) return;

  const isDead = state.player.death.isDead;
  const canDuel = !isDead && !state.player.duel.active && !state.player.combatState.active && !other.isDead;
  const canTrade = !isDead && !other.isDead;
  const canAttack = !isDead && state.player.pvp.flagged && other.pvpFlagged;

  const menu = state.contextMenu;
  menu.open = true;
  menu.x = screenX;
  menu.y = screenY;
  menu.targetId = playerId;
  menu.targetType = "player";
  menu.targetName = other.name;
  menu.options = [
    { id: "duel", label: "Duel", icon: "\u2694", enabled: canDuel },
    { id: "party_invite", label: "Invite to Party", icon: "\ud83d\udc65", enabled: !isDead },
    { id: "trade", label: "Trade", icon: "\ud83d\udce6", enabled: canTrade },
    { id: "whisper", label: "Whisper", icon: "\ud83d\udcac", enabled: true },
    { id: "inspect", label: "Inspect", icon: "\ud83d\udd0d", enabled: true },
    { id: "set_focus", label: "Set Focus", icon: "\ud83d\udd2d", enabled: true },
    { id: "follow", label: "Follow", icon: "\ud83d\udc63", enabled: !isDead },
    { id: "target", label: "Target", icon: "\u25ce", enabled: true },
    { id: "mark_skull", label: "Mark: Skull", icon: "\ud83d\udc80", enabled: true },
    { id: "attack", label: "Attack", icon: "\u2694", enabled: canAttack, color: "#e04040" },
    { id: "report", label: "Report", icon: "\u26a0", enabled: true, color: "#a0855a" },
  ];
}

/** Close the context menu. */
export function closeContextMenu(state: GameState): void {
  state.contextMenu.open = false;
  state.contextMenu.options = [];
  state.contextMenu.hoverIdx = -1;
}

// ─── Honor / Dishonor System ─────────────────────────────────────────────────

const HONOR_RANKS = [
  0, 15, 50, 120, 250, 500, 1000, 2000, 3500, 5500, 8000, 12000, 17000, 23000, 30000,
];
const HONOR_RANK_NAMES = [
  "Unranked", "Private", "Corporal", "Sergeant", "Master Sergeant",
  "Sergeant Major", "Knight", "Knight-Lieutenant", "Knight-Captain",
  "Knight-Champion", "Lieutenant Commander", "Commander", "Marshal",
  "Field Marshal", "Grand Marshal",
];

/**
 * Award honor or dishonor after a PvP kill.
 * Fair kills (opponent within 3 levels) grant honor.
 * Ganking (opponent 5+ levels lower) incurs dishonor.
 * Killing the same player repeatedly gives diminishing returns.
 */
export function awardPvPHonor(
  state: GameState,
  opponentLevel: number,
): void {
  const pvp = state.player.pvp;
  const myLevel = state.player.combat.level;
  const levelDiff = myLevel - opponentLevel;

  if (levelDiff >= 5) {
    // Ganking — dishonor
    const dishonorGain = 5 + Math.floor(levelDiff * 2);
    pvp.dishonor += dishonorGain;
    addToast(state, `−${dishonorGain} Dishonor (unfair kill)`, "pvp");
  } else if (levelDiff >= -3 && levelDiff <= 3) {
    // Fair fight — full honor
    const baseHonor = 10 + opponentLevel * 2;
    const honor = Math.round(baseHonor * (levelDiff <= 0 ? 1.2 : 1.0));
    pvp.honor += honor;
    addToast(state, `+${honor} Honor`, "pvp");
    updateHonorRank(state);
  } else {
    // Opponent much higher level — bonus honor
    const honor = Math.round(15 + opponentLevel * 3);
    pvp.honor += honor;
    addToast(state, `+${honor} Honor (valiant kill!)`, "pvp");
    updateHonorRank(state);
  }
}

/** Check if the player's honor warrants a rank promotion. */
function updateHonorRank(state: GameState): void {
  const pvp = state.player.pvp;
  // Dishonor reduces effective honor for ranking
  const effectiveHonor = Math.max(0, pvp.honor - pvp.dishonor * 2);
  let newRank = 0;
  for (let i = HONOR_RANKS.length - 1; i >= 0; i--) {
    if (effectiveHonor >= HONOR_RANKS[i]) {
      newRank = i;
      break;
    }
  }
  if (newRank > pvp.honorRank) {
    pvp.honorRank = newRank;
    addToast(state, `Rank Up! ${HONOR_RANK_NAMES[newRank]}`, "pvp");
  } else if (newRank < pvp.honorRank) {
    pvp.honorRank = newRank;
    addToast(state, `Rank reduced to ${HONOR_RANK_NAMES[newRank]}`, "pvp");
  }
}

/** Get the display name for the player's current honor rank. */
export function getHonorRankName(state: GameState): string {
  return HONOR_RANK_NAMES[state.player.pvp.honorRank] ?? "Unranked";
}
